import React, { useEffect, useState } from "react";
import Helmet from "react-helmet";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";
import Socials from "../components/about/socials";
import StatusIndicator from "../components/common/statusIndicator";

import INFO from "../data/user";
import SEO from "../data/seo";
import { FORMSPREE_ENDPOINT, FORMSPREE_READY } from "../data/contactConfig";
import { track } from "../lib/analytics";

import "./styles/contact.css";

const Contact = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "contact");

	// "idle" | "sending" | "sent" | "error"
	const [status, setStatus] = useState("idle");

	const handleSubmit = async (e) => {
		e.preventDefault();
		const form = e.target;
		const data = new FormData(form);

		// No endpoint configured yet: fall back to the user's mail client so the
		// page still works, and record the intent.
		if (!FORMSPREE_READY) {
			track("contact-submit", { via: "mailto-fallback" });
			const subject = encodeURIComponent(
				`Portfolio contact from ${data.get("name") || "someone"}`
			);
			const body = encodeURIComponent(
				`${data.get("message") || ""}\n\n— ${data.get("name") || ""} (${
					data.get("email") || ""
				})`
			);
			window.location.href = `mailto:${INFO.main.email}?subject=${subject}&body=${body}`;
			return;
		}

		setStatus("sending");
		try {
			const res = await fetch(FORMSPREE_ENDPOINT, {
				method: "POST",
				body: data,
				headers: { Accept: "application/json" },
			});
			if (res.ok) {
				setStatus("sent");
				track("contact-submit", { via: "formspree" });
				form.reset();
			} else {
				setStatus("error");
			}
		} catch (err) {
			setStatus("error");
		}
	};

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Contact | ${INFO.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta
					name="keywords"
					content={currentSEO.keywords.join(", ")}
				/>
			</Helmet>

			<div className="page-content">
				<NavBar active="contact" />
				<div className="content-wrapper">
					<div className="contact-logo-container">
						<div className="contact-logo">
							<Logo width={54} />
						</div>
					</div>

					<div className="contact-container">
						<div className="contact-lead">
							<div className="title contact-title">
								Let's Build Something Great Together
							</div>

							<div className="subtitle contact-subtitle">
								Ready to discuss your next technical challenge,
								team scaling opportunity, or engineering
								leadership role? I'm always interested in
								connecting with fellow technical leaders,
								innovative companies, and ambitious teams.
								<br />
								<br />
								<StatusIndicator
									status="available"
									message="Available for engineering leadership roles & consulting"
								/>
							</div>

							{status === "sent" ? (
								<div className="contact-form-sent">
									Thanks, your message is on its way. I aim to
									reply within 24 to 48 hours.
								</div>
							) : (
								<form
									className="contact-form"
									onSubmit={handleSubmit}
								>
									{/* Sets the subject line of the email
									    Formspree sends me. */}
									<input
										type="hidden"
										name="_subject"
										value="New message from swapniltamse.com"
									/>
									{/* Honeypot: bots fill this hidden field, and
									    Formspree drops any submission that has it
									    filled. Real users never see it. */}
									<input
										type="text"
										name="_gotcha"
										tabIndex={-1}
										autoComplete="off"
										aria-hidden="true"
										style={{ display: "none" }}
									/>
									<label className="contact-field">
										<span className="contact-field-label">
											Name
										</span>
										<input
											type="text"
											name="name"
											required
											autoComplete="name"
										/>
									</label>
									<label className="contact-field">
										<span className="contact-field-label">
											Email
										</span>
										<input
											type="email"
											name="email"
											required
											autoComplete="email"
											placeholder="you@company.com"
										/>
									</label>
									<label className="contact-field">
										<span className="contact-field-label">
											Message
										</span>
										<textarea
											name="message"
											rows={5}
											required
											placeholder="What are you working on?"
										/>
									</label>
									<button
										className="contact-submit"
										type="submit"
										disabled={status === "sending"}
									>
										{status === "sending"
											? "Sending..."
											: "Send message"}
									</button>
									{status === "error" && (
										<div className="contact-form-error">
											Something went wrong. You can also
											email me directly at{" "}
											<a
												href={`mailto:${INFO.main.email}`}
											>
												{INFO.main.email}
											</a>
											.
										</div>
									)}
								</form>
							)}

							<div className="contact-email-line">
								Prefer email?{" "}
								<a
									href={`mailto:${INFO.main.email}`}
									onClick={() =>
										track("contact-email-click")
									}
								>
									{INFO.main.email}
								</a>
							</div>
						</div>
					</div>

					<a
						className="contact-moma"
						href="https://www.why.design/project/moma-belong-campaign"
						target="_blank"
						rel="noopener noreferrer"
						title="MOMA Belong to Something"
					>
						<img
							src={process.env.PUBLIC_URL + "/moma-contact.jpg"}
							alt="MOMA Belong to Something"
						/>
					</a>

					<div className="about-socials">
						<Socials />
					</div>
					<div className="about-socials-mobile">
						<Socials />
					</div>
					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Contact;
